import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getBillingNotificationTarget,
  getInvoiceNotifications,
} from '../utils/billingNotificationApi';

const ALLOWED_ROLES = new Set(['Admin', 'Owner', 'Store Owner', 'Super Admin']);
const EVENT_TYPES = new Set([
  'created',
  'first_sent',
  'paid',
  'cancelled',
  'send_failed',
  'overdue',
]);
const REFRESH_INTERVAL = 5 * 60 * 1000;
const STALE_INTERVAL = 60 * 1000;
const BillingNotificationSessionContext = createContext(null);

const initialValue = {
  identityKey: null,
  status: 'idle',
  session: null,
  activePastDue: [],
  notifications: [],
  source: null,
  error: null,
  errorKind: null,
};

const validStoreId = (id) =>
  (typeof id === 'string' || typeof id === 'number') && String(id).trim() !== '';

const isRealDateOnly = (value) => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
};

const isRealTimestamp = (value) =>
  typeof value === 'string' &&
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(value) &&
  isRealDateOnly(value.slice(0, 10)) &&
  Number.isFinite(Date.parse(value));

class BillingNotificationResponseError extends Error {}

const validInvoiceFields = (item) =>
  item &&
  Number.isInteger(item.invoiceId) &&
  item.invoiceId > 0 &&
  (item.invoiceNumber == null ||
    (Number.isInteger(item.invoiceNumber) && item.invoiceNumber > 0)) &&
  typeof item.invoiceStatus === 'string' &&
  item.invoiceStatus.trim() !== '' &&
  Number.isInteger(item.clientId) &&
  item.clientId > 0 &&
  typeof item.clientName === 'string' &&
  item.clientName.trim() !== '' &&
  (item.contactEmail === null || typeof item.contactEmail === 'string') &&
  typeof item.totalAmount === 'number' &&
  Number.isFinite(item.totalAmount) &&
  isRealDateOnly(item.dueDate) &&
  getBillingNotificationTarget(item);

const readNotifications = (payload) => {
  if (!payload || payload.success !== true) {
    throw new BillingNotificationResponseError(
      typeof payload?.message === 'string' && payload.message.trim()
        ? payload.message
        : 'Unable to load billing notifications.',
    );
  }
  if (
    !Array.isArray(payload.notifications) ||
    !Array.isArray(payload.activePastDue) ||
    !Number.isInteger(payload.activePastDueCount) ||
    payload.activePastDueCount !== payload.activePastDue.length
  ) {
    throw new BillingNotificationResponseError('The billing notification response was incomplete.');
  }
  const invoiceIds = new Set();
  const activePastDue = payload.activePastDue.map((invoice) => {
    if (
      !Number.isInteger(invoice?.id) ||
      invoice.id < 1 ||
      invoice.id !== invoice.invoiceId ||
      !validInvoiceFields(invoice) ||
      !['Sent', 'Overdue'].includes(invoice.invoiceStatus) ||
      invoiceIds.has(invoice.invoiceId)
    ) {
      throw new BillingNotificationResponseError(
        'The billing notification response contained an invalid past-due invoice.',
      );
    }
    invoiceIds.add(invoice.invoiceId);
    return invoice;
  });
  const notifications = payload.notifications.map((notification) => {
    if (
      !notification ||
      !Number.isInteger(notification.id) ||
      notification.id < 1 ||
      !EVENT_TYPES.has(notification.eventType) ||
      typeof notification.eventKey !== 'string' ||
      notification.eventKey.trim() === '' ||
      !isRealTimestamp(notification.eventTime) ||
      !validInvoiceFields(notification) ||
      (notification.failureSummary !== null && typeof notification.failureSummary !== 'string') ||
      (notification.eventType === 'send_failed' &&
        (typeof notification.failureSummary !== 'string' ||
          notification.failureSummary.trim() === '')) ||
      !Number.isInteger(notification.occurrenceCount) ||
      notification.occurrenceCount < 1 ||
      (notification.resolvedAt !== null && !isRealTimestamp(notification.resolvedAt))
    ) {
      throw new BillingNotificationResponseError(
        'The billing notification response contained an invalid notification.',
      );
    }
    return notification;
  });
  return { activePastDue, notifications, source: payload.source ?? null };
};

const responseMessage = (error, fallback) => {
  const message = error?.response?.data?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
};

export const BillingNotificationSessionProvider = ({ children }) => {
  const user = useSelector((state) => state.user);
  const selectedStoreId = useSelector((state) => state.stores?.selectedStore?.storeId);
  const [value, setValue] = useState(initialValue);
  const requestVersion = useRef(0);
  const requestController = useRef(null);
  const inFlight = useRef(null);
  const lastRequestAt = useRef(null);
  const locallyEligible =
    user?.isAuthenticated === true &&
    user?.loading !== true &&
    user?.isImpersonating !== true &&
    ALLOWED_ROLES.has(user?.role);
  const identityKey = JSON.stringify([
    user?.id ?? null,
    user?.email ?? null,
    user?.role ?? null,
    user?.storeId ?? null,
    user?.clientId ?? null,
    user?.isImpersonating === true,
    selectedStoreId ?? null,
  ]);

  const load = useCallback(async (requestedIdentityKey, requestedStoreId, requestedRole) => {
    if (inFlight.current?.key === requestedIdentityKey) return inFlight.current.promise;
    requestController.current?.abort();
    const controller = new AbortController();
    requestController.current = controller;
    const version = ++requestVersion.current;
    lastRequestAt.current = Date.now();
    setValue({ ...initialValue, identityKey: requestedIdentityKey, status: 'loading' });

    const session = { storeId: requestedStoreId, role: requestedRole };
    const promise = (async () => {
      try {
        const payload = await getInvoiceNotifications(session.storeId, controller.signal);
        if (version !== requestVersion.current || controller.signal.aborted) return;
        setValue({
          identityKey: requestedIdentityKey,
          status: 'ready',
          session,
          ...readNotifications(payload),
          error: null,
          errorKind: null,
        });
      } catch (error) {
        if (version !== requestVersion.current || controller.signal.aborted) return;
        const status = error?.response?.status;
        setValue({
          ...initialValue,
          identityKey: requestedIdentityKey,
          status: 'error',
          session,
          errorKind: status === 401 ? 'authentication' : status === 403 ? 'authorization' : 'load',
          error:
            status === 401
              ? 'Your session has expired. Please sign in again.'
              : status === 403
                ? 'You do not have access to billing notifications for this store.'
                : error instanceof BillingNotificationResponseError
                  ? error.message
                  : responseMessage(
                      error,
                      'Unable to load billing notifications. Please try again.',
                    ),
        });
      }
    })();
    inFlight.current = { key: requestedIdentityKey, promise };
    try {
      await promise;
    } finally {
      if (inFlight.current?.promise === promise) inFlight.current = null;
    }
  }, []);

  useEffect(() => {
    requestController.current?.abort();
    requestVersion.current += 1;
    inFlight.current = null;
    lastRequestAt.current = null;
    setValue(initialValue);
    if (!locallyEligible || !validStoreId(selectedStoreId)) return undefined;
    load(identityKey, selectedStoreId, user.role);
    return () => {
      requestController.current?.abort();
      requestVersion.current += 1;
      inFlight.current = null;
    };
  }, [identityKey, load, locallyEligible, selectedStoreId, user?.role]);

  const retry = useCallback(() => {
    if (locallyEligible && validStoreId(selectedStoreId))
      load(identityKey, selectedStoreId, user.role);
  }, [identityKey, load, locallyEligible, selectedStoreId, user?.role]);

  const refreshIfStale = useCallback(() => {
    if (
      locallyEligible &&
      validStoreId(selectedStoreId) &&
      document.visibilityState !== 'hidden' &&
      (lastRequestAt.current === null || Date.now() - lastRequestAt.current >= STALE_INTERVAL)
    ) {
      load(identityKey, selectedStoreId, user.role);
    }
  }, [identityKey, load, locallyEligible, selectedStoreId, user?.role]);

  useEffect(() => {
    if (!locallyEligible) return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshIfStale();
    };
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'hidden') refreshIfStale();
    }, REFRESH_INTERVAL);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [locallyEligible, refreshIfStale]);

  const visibleValue = locallyEligible && value.identityKey === identityKey ? value : initialValue;

  return (
    <BillingNotificationSessionContext.Provider value={{ ...visibleValue, retry, refreshIfStale }}>
      {children}
    </BillingNotificationSessionContext.Provider>
  );
};

export const useBillingNotificationSession = () => {
  const context = useContext(BillingNotificationSessionContext);
  if (!context) {
    throw new Error(
      'useBillingNotificationSession must be used within BillingNotificationSessionProvider.',
    );
  }
  return context;
};
